import type { PageHelp } from "../../help/types";

export const dashboardHelp: PageHelp = {
  title: "Dashboard",
  intro:
    "What your benefits cost right now: the total of the packages that are active today, split into what your employer pays and what you pay for yourself and for your dependents.",
  sections: ["Overview", "Totals", "Breakdown"],
  tips: [
    "Only packages that are active today are counted — the period between their start and end date includes today. A package whose cancellation date has already passed is left out; one cancelled for a future date still counts until then.",
    "When a package's cost split is set as percentages, the amounts shown are those percentages applied to the package price.",
    "Amounts in different currencies are never added together or converted. Each currency has its own view; switch between them with the currency buttons.",
  ],
  steps: [
    {
      selector: '[data-help="dashboard-empty-state"]',
      title: "Find a benefit to enrol in",
      body: "Shown instead of the figures while none of your packages is active today. The link in the sentence opens the Benefits page on the packages you are eligible for.",
      section: "Overview",
      optional: true,
    },
    {
      selector: '[data-help="dashboard-message"]',
      title: "Read your employer's message",
      body: "A short message from your employer, or a standard welcome when there is none. Long messages are cut off; point at the text to read it in full.",
      section: "Overview",
    },
    {
      selector: '[data-help="dashboard-currency"]',
      title: "Choose the currency to show",
      body: "Appears only when your active packages are priced in more than one currency. Click a currency to show the figures in it; the highlighted one is shown now. Amounts are not converted, so each currency shows only the packages priced in it.",
      section: "Overview",
      optional: true,
    },
    {
      selector: '[data-help="dashboard-total-card"]',
      title: "Read the total cost of your benefits",
      body: "The full price of all your active packages in the selected currency, for you and your dependents together — your employer's share plus yours.",
      section: "Totals",
    },
    {
      selector: '[data-help="dashboard-your-share-card"]',
      title: "Read what you pay yourself",
      body: "The part of the total that you pay — for your own packages and for those of your dependents. The rest is paid by your employer.",
      section: "Totals",
    },
    {
      selector: '[data-help="dashboard-per-category"]',
      title: "Compare costs per category",
      body: "The total cost per benefit category (the categories your employer uses, such as health or mobility), each bar showing the category's share of the overall total. At most four categories are listed.",
      section: "Totals",
    },
    {
      selector: '[data-help="dashboard-financial-chart"]',
      title: "See who pays how much",
      body: "Splits the total into what your employer pays and what you pay for yourself and your dependents. The bigger the box, the bigger the amount; the exact amounts are listed underneath, and pointing at a box shows its value.",
      section: "Breakdown",
    },
    {
      selector: '[data-help="dashboard-employee-chart"]',
      title: "See the cost of your own packages",
      body: "Only the packages you are enrolled in yourself: your employer's share, your share and their total.",
      section: "Breakdown",
    },
    {
      selector: '[data-help="dashboard-dependent-chart"]',
      title: "See the cost of your dependents' packages",
      body: "Only the packages your dependents are enrolled in: what your employer pays for them, what you pay for them, and the total. All zero when no dependent is enrolled in anything.",
      section: "Breakdown",
    },
  ],
};
